import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';

import { useNotifications } from '../../hooks/useNotifications';
import styles from './Notification.module.css';
import NotificationToast from './NotificationToast';

/**
 * Whether this browser implements the HTML popover API.
 *
 * Feature-detected at call time rather than at module load so the check stays
 * honest under test, where the API is polyfilled per-suite exactly as
 * `HTMLDialogElement.showModal` already is.
 *
 * Support is broad — Chrome 114+, Safari 17+, Firefox 125+ — but the fallback
 * matters anyway: on an engine without it the `popover` attribute is never
 * set, no top-layer call is made, and the container renders exactly as it did
 * before (a `position: fixed` element at `z-index: 600`). An unsupported
 * browser is therefore no worse off than it is today.
 */
const supportsPopover = (): boolean =>
  typeof HTMLElement !== 'undefined' && typeof HTMLElement.prototype.showPopover === 'function';

const NotificationContainer: React.FC = () => {
  const { notifications, removeNotification } = useNotifications();
  const containerRef = useRef<HTMLDivElement>(null);
  // Mirrors the element's popover state. Tracked here rather than read back
  // via `matches(':popover-open')` because that selector throws outright on
  // engines that do not know it, which would turn a graceful degradation into
  // a crash.
  const isInTopLayerRef = useRef(false);

  const hasNotifications = notifications.length > 0;

  // A toast raised from a modal <dialog> is otherwise painted *underneath* it:
  // `showModal()` puts the dialog in the browser's top layer, and no `z-index`
  // on an ordinary positioned element can reach above that. The result is a
  // toast dimmed by the dialog's backdrop whose dismiss control cannot be
  // clicked — precisely the failure paths where the message matters most.
  // Promoting the container into the top layer too puts it back above the
  // dialog and restores pointer access.
  //
  // `manual` rather than `auto`: an auto popover is light-dismissed by any
  // outside click, so it would vanish the moment the admin touched the dialog
  // underneath it. Manual also keeps the container out of the popover stack,
  // so it never takes focus — toasts are announced to assistive tech via
  // aria-live, never focused.
  useEffect(() => {
    const element = containerRef.current;
    if (!element || !supportsPopover()) return;

    if (hasNotifications && !isInTopLayerRef.current) {
      element.showPopover();
      isInTopLayerRef.current = true;
    } else if (!hasNotifications && isInTopLayerRef.current) {
      element.hidePopover();
      isInTopLayerRef.current = false;
    }
  }, [hasNotifications]);

  // `@types/react` 18 predates the popover API, so `popover` is not yet in
  // JSX.IntrinsicElements. A locally typed props object keeps this strictly
  // typed without a global module augmentation or an `any` escape hatch.
  // Omitted entirely when unsupported, so the attribute selectors in the
  // stylesheet cannot match and the pre-existing layout rules stay in force.
  const popoverProps: { popover?: 'manual' } = supportsPopover() ? { popover: 'manual' } : {};

  // Portalled onto document.body, outside the app root: a sheet's `inert`
  // makes everything inside the app root non-focusable and non-clickable
  // while it is open, which would otherwise silence and freeze a toast
  // raised from inside that sheet (e.g. a cart refusal error).
  return createPortal(
    <div ref={containerRef} className={styles.container} {...popoverProps}>
      {notifications.map((notification) => (
        <NotificationToast
          key={notification.id}
          notification={notification}
          onRemove={removeNotification}
        />
      ))}
    </div>,
    document.body,
  );
};

export default NotificationContainer;
